/**
 * RegionService — AMB-224 follow-up.
 *
 * Wraps the anonymous `GET /api/region` lookup with the same 24h
 * localStorage/AsyncStorage cache pattern as `services/timezone.ts`,
 * so guest viewers don't repeat the GeoIP call on every paint.
 *
 * Authenticated callers don't need this — they read `user.region`
 * straight off `/api/auth/me`. This module exists for the
 * before-login window: `/masters`, public profile pages, share-link
 * landing — where the FE still needs to gate Instagram/Facebook UI
 * via `isRestrictedRegion()`.
 *
 * Fails silently to `null` (= "no signal") so the gating helper
 * falls back to the timezone path. Never throws into a render tree.
 */

import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { api } from './api';

const CACHE_KEY = 'agape_user_region';
const CACHE_TS_KEY = 'agape_user_region_ts';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

async function readCache(key: string): Promise<string | null> {
  if (Platform.OS === 'web') return localStorage.getItem(key);
  return AsyncStorage.getItem(key);
}

async function writeCache(key: string, value: string): Promise<void> {
  if (Platform.OS === 'web') {
    localStorage.setItem(key, value);
  } else {
    await AsyncStorage.setItem(key, value);
  }
}

/**
 * Returns the ISO 3166-1 alpha-2 country code for the current request
 * (e.g. "RU", "US") — or `null` when GeoIP can't resolve or the network
 * call fails. First call hits `/api/region`; subsequent calls within
 * 24h read the cache without a network request.
 *
 * The empty-string sentinel value `""` is stored when the server
 * authoritatively returned `null` (GeoIP couldn't determine — common
 * in dev). That way we cache the negative answer too and don't refetch
 * every paint, but `getCachedRegion()` still returns `null` cleanly.
 */
export async function getUserRegion(): Promise<string | null> {
  try {
    const cached = await readCache(CACHE_KEY);
    const cachedTs = await readCache(CACHE_TS_KEY);
    if (cached !== null && cachedTs) {
      const age = Date.now() - parseInt(cachedTs, 10);
      if (age < CACHE_TTL_MS) {
        return cached === '' ? null : cached;
      }
    }
    const { country } = await api.getRegion();
    const toStore = country ?? '';
    await writeCache(CACHE_KEY, toStore);
    await writeCache(CACHE_TS_KEY, String(Date.now()));
    return country ?? null;
  } catch {
    // Network down / 5xx / parse error — leave the cache alone so a
    // retry on the next paint cycle has a chance to succeed.
    return null;
  }
}

/**
 * Synchronous read of the cached region, for use inside React renders
 * where `await` is not an option. Returns `null` if nothing is cached
 * yet — callers should still kick off `getUserRegion()` once on mount
 * (typically inside an effect) to populate the cache.
 */
export function getCachedRegion(): string | null {
  if (Platform.OS === 'web') {
    const v = localStorage.getItem(CACHE_KEY);
    if (v === null) return null;
    return v === '' ? null : v;
  }
  // On native, AsyncStorage is async-only; we can't peek synchronously.
  // Callers must rely on the user.region path or the async getUserRegion.
  return null;
}
